import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../../components/button/button.component';
import {
  GalleryComponent,
  TranslatedProject,
} from '../../components/gallery/gallery.component';
import { TestimonialsComponent } from '../../components/testimonials/testimonials.component';
import { ScrollService } from '../../services/scroll.service';
import { TranslationService } from '../../services/translation.service';
import { ALL_PROJECTS, Project } from '../projects/projects.data';

@Component({
  selector: 'app-home',
  standalone: true,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [ButtonComponent, GalleryComponent, TestimonialsComponent],
})
export class HomeComponent implements OnInit, AfterViewInit {
  @ViewChild('projects') projects!: ElementRef;
  @ViewChild('aboutMeSection') aboutMeSection!: ElementRef;
  @ViewChild('contactSection') contactSection!: ElementRef;

  // Elementos de Imagem e Layout
  imageLeft = '../../../assets/images/julio_graffin_cover.png';
  imageRight = '../../../assets/images/source_code_cover.png';

  // Injeções de Serviços
  translate = inject(TranslationService);
  scrollService = inject(ScrollService);
  destroyRef = inject(DestroyRef);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  // Estados Privados (Fontes da verdade brutas)
  private rawFeaturedProject = signal<Project | null>(null);

  // Lista Estática Localizada
  testimonialsList = [
    {
      text: 'Recomendo seu trabalho com confiança para projetos frontend e modernização de aplicações Angular.',
      authorName: 'Fabrício Fernandes',
      role: 'Arquiteto de soluções | Stefanini Brasil',
      avatarUrl: '../../../assets/images/fabricio.png',
      profileUrl:
        'https://www.linkedin.com/in/fabricio-fernandes-freitas-83708672/',
    },
    {
      text: 'Sempre preocupado com a qualidade das suas entregas, boas práticas em padrões, tokens e fera em frameworks. Alem disso é uma pessoa muito honesta, sincera, calma e dedicada.',
      authorName: 'Alexandre Stresser',
      role: 'Product Designer Sênior',
      avatarUrl: '../../../assets/images/alexandre.png',
      profileUrl: 'https://www.linkedin.com/in/alexandre-stresser-486987156/',
    },
    {
      text: 'Júlio Graffin demonstrou profissionalismo, comprometimento e excelência no desenvolvimento front-end, colaborando de forma próxima com o time de Design para entregar soluções de alta qualidade.',
      authorName: 'Peterson Freitas',
      role: 'Digital Experience Design Lead | Electrolux',
      avatarUrl: '../../../assets/images/peterson.png',
      profileUrl: 'https://www.linkedin.com/in/silvapeterson/',
    },
  ];

  // 1. Computed que traduz dinamicamente todos os projetos da base de dados
  allProjects = computed<TranslatedProject[]>(() => {
    const currentLang = this.translate.currentLang() as
      | 'pt-br'
      | 'en-us'
      | 'es-es';

    return ALL_PROJECTS.map((project) => ({
      id: project.id,
      slug: project.slug,
      image: project.image,
      fullImage: project.fullImage,
      icon: project.icon,
      labels: project.labels,
      ...(project.languages[currentLang] || project.languages['pt-br']),
    }));
  });

  // 2. Transforma o projeto bruto destacado em uma versão traduzida baseada na lista acima
  featuredProject = computed<TranslatedProject | null>(() => {
    const featuredRaw = this.rawFeaturedProject();
    if (!featuredRaw) return null;

    return this.allProjects().find((p) => p.id === featuredRaw.id) || null;
  });

  // 3. Filtra as miniaturas mantendo todos os outros 4 projetos intocados na lista
  // 3. Filters the thumbnails, shuffles them randomly, and limits the list to exactly 4 items
  thumbnailsProjects = computed<TranslatedProject[]>(() => {
    const featured = this.featuredProject();
    const projects = this.allProjects();

    if (!featured) return [];

    // 1. Filter out the currently featured project
    const remainingProjects = projects.filter((p) => p.id !== featured.id);

    // 2. Shuffle the remaining array randomly
    // (Using a simple sort-by-random approach, or a Fisher-Yates shuffle if you prefer)
    const shuffled = [...remainingProjects].sort(() => Math.random() - 0.5);

    // 3. Extract exactly 4 projects for the small thumbnail cards
    return shuffled.slice(0, 4);
  });

  // home.component.ts

  // home.component.ts

  ngOnInit() {
    this.setRandomFeaturedProject();

    // This single subscription will now handle clicks from BOTH home and external routes perfectly
    this.scrollService.scroll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sectionId) => {
        this.handleScrollExecution(sectionId);
      });
  }

  // Remove ngAfterViewInit completely if you aren't using it for anything else!

  ngAfterViewInit() {
    // Scroll listener for when we ARRIVE from another route
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const sectionId = params['scroll'];
        if (sectionId) {
          // Wait for Angular change detection, then wait for browser paint frame
          Promise.resolve().then(() => {
            requestAnimationFrame(() => {
              this.handleScrollExecution(sectionId);

              // Clean up the URL query parameter safely
              this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { scroll: null },
                queryParamsHandling: 'merge',
              });
            });
          });
        }
      });
  }

  private setRandomFeaturedProject() {
    if (ALL_PROJECTS.length > 0) {
      const randomIndex = Math.floor(Math.random() * ALL_PROJECTS.length);
      this.rawFeaturedProject.set(ALL_PROJECTS[randomIndex]);
    }
  }

  projectSelected(item: TranslatedProject) {
    this.router.navigate(['/projetos', item.slug]);
  }

  // Helper method to keep your ngOnInit clean
  private handleScrollExecution(sectionId: string) {
    if (sectionId === 'projects') {
      this.scrollToProjects();
    } else if (sectionId === 'about-me') {
      this.scrollToAboutMe();
    } else if (sectionId === 'contact') {
      this.scrollToContact();
    }
  }

  scrollToProjects() {
    // Fallback: if ViewChild isn't ready, try grabbing it by ID
    const element =
      this.projects?.nativeElement || document.getElementById('projects');
    if (element) {
      this.scrollToElementWithOffset(element);
    } else {
      console.warn('Could not find projects element in DOM.');
    }
  }

  scrollToAboutMe() {
    const element =
      this.aboutMeSection?.nativeElement || document.getElementById('about-me');
    if (element) {
      this.scrollToElementWithOffset(element);
    } else {
      console.warn('Could not find about-me element in DOM.');
    }
  }

  scrollToContact() {
    const element =
      this.contactSection?.nativeElement || document.getElementById('contact');
    if (element) {
      this.scrollToElementWithOffset(element);
    } else {
      console.warn('Could not find contact element in DOM.');
    }
  }

  private scrollToElementWithOffset(element: HTMLElement) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
}
